import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { Doughnut, Pie } from "react-chartjs-2";
import "chart.js/auto"; // Required for Pie chart rendering
import { AUDIT_AGENT_PERFORMANCE } from "../../constants";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

const AgentOverallPerformanceComponent = ({ data, filters }) => {

  const navigate = useNavigate();

  const handleOpenClick = () => {
    navigate("/remark_details_report", { state: { activeTab: 0, prevFilters: filters } }); // Pass `activeTab` as state
  };

  const handleClosedClick = () => {
    navigate("/remark_details_report", { state: { activeTab: 1, prevFilters: filters } }); // Pass `activeTab` as state
  };

  const handleCompletedClick = () => {
    navigate("/followup_details_report", { state: { activeTab: 0, prevFilters: filters } }); // Pass `activeTab` as state
  };

  const handlePendingClick = () => {
    navigate("/followup_details_report", { state: { activeTab: 1, prevFilters: filters } }); // Pass `activeTab` as state
  };

  return (
    <>
      <Box p={3}>
        {data ? (
          <>
            {/* Summary Section */}
            <Grid container spacing={3}>
              <Grid container spacing={3}>
                {/* Combined Cards: Total Remarks, Open/Closed Follow-ups */}
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      textAlign: "center",
                      borderLeft: "4px solid rgba(75, 192, 192, 1)",
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" color="primary">
                        Total Remarks Done
                      </Typography>
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        color="secondary"
                        gutterBottom
                      >
                        {data?.summary?.total_remarks || 0}
                      </Typography>
                    </CardContent>
                  </Card>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Card
                        sx={{
                          textAlign: "center",
                          mt: 2,
                          borderLeft: "4px solid rgba(75, 192, 192, 1)",
                        }}
                        onClick={handleOpenClick}
                      >
                        <CardContent>
                          <Typography variant="h6" color="primary">
                            Open
                          </Typography>
                          <Typography
                            variant="h4"
                            fontWeight="bold"
                            color="secondary"
                            gutterBottom
                          >
                            {(data?.summary?.status_wise_count?.Open || data?.summary?.status_wise_count?.open || 0)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card
                        sx={{
                          textAlign: "center",
                          mt: 2,
                          borderLeft: "4px solid rgba(75, 192, 192, 1)",
                        }}
                        onClick={handleClosedClick}
                      >
                        <CardContent>
                          <Typography variant="h6" color="primary">
                            Closed
                          </Typography>
                          <Typography
                            variant="h4"
                            fontWeight="bold"
                            color="secondary"
                            gutterBottom
                          >
                            {(data?.summary?.status_wise_count?.Closed || data?.summary?.status_wise_count?.closed || 0)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Combined Cards: Total Follow-up, Completed/Pending Follow-ups */}
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      textAlign: "center",
                      borderLeft: "4px solid rgb(77, 75, 192)",
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" color="primary">
                        Total Follow-up
                      </Typography>
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        color="secondary"
                        gutterBottom
                      >
                        {data?.summary?.followup_statistics?.total_followups ??
                          0}
                      </Typography>
                    </CardContent>
                  </Card>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Card
                        sx={{
                          textAlign: "center",
                          mt: 2,
                          borderLeft: "4px solid rgb(77, 75, 192)",
                        }}
                        onClick={handleCompletedClick}
                      >
                        <CardContent>
                          <Typography variant="h6" color="primary">
                            Completed
                          </Typography>
                          <Typography
                            mt={1}
                            fontWeight="bold"
                            color="secondary"
                            gutterBottom
                          >
                            {
                              data?.summary?.followup_statistics
                                ?.completed_followups
                            }{" "}
                            /{" "}
                            {
                              data?.summary?.followup_statistics
                                ?.total_followups
                            }
                          </Typography>
                          <Box
                            sx={{
                              width: "100%",
                              bgcolor: "#cfd8dc",
                              borderRadius: 5, // Adjust the value for more rounded corners
                              height: 10,
                              mt: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: `${
                                  (data?.summary?.followup_statistics
                                    ?.completed_followups /
                                    data?.summary?.followup_statistics
                                      ?.total_followups) *
                                  100
                                }%`,
                                bgcolor: "#66bb6a",
                                height: "100%",
                                borderRadius: 5, // Ensure the inner progress bar is also rounded
                              }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card
                        sx={{
                          textAlign: "center",
                          mt: 2,
                          borderLeft: "4px solid rgb(77, 75, 192)",
                        }}
                        onClick={handlePendingClick}
                      >
                        <CardContent>
                          <Typography variant="h6" color="primary">
                            Pending
                          </Typography>
                          <Typography
                            mt={1}
                            fontWeight="bold"
                            color="secondary"
                            gutterBottom
                          >
                            {
                              data?.summary?.followup_statistics
                                ?.pending_followups
                            }{" "}
                            /{" "}
                            {
                              data?.summary?.followup_statistics
                                ?.total_followups
                            }
                          </Typography>
                          <Box
                            sx={{
                              width: "100%",
                              bgcolor: "#cfd8dc",
                              borderRadius: 5, // Adjust the value for more rounded corners
                              height: 10,
                              mt: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: `${
                                  (data?.summary?.followup_statistics
                                    ?.pending_followups /
                                    data?.summary?.followup_statistics
                                      ?.total_followups) *
                                  100
                                }%`,
                                bgcolor: "#ef5350",
                                height: "100%",
                                borderRadius: 5, // Ensure the inner progress bar is also rounded
                              }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Status-wise Count (Pie Chart) */}
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                    }}
                  >
                    <CardContent sx={{ padding: "8px" }}>
                      <Typography variant="h6" color="primary" align="center">
                        Status-wise Summary
                      </Typography>
                      <Box
                        sx={{
                          height: "200px",
                          width: "100%",
                          placeItems: "center",
                          marginTop: 2
                        }}
                      >
                        <Doughnut
                          data={{
                            labels: ["Open", "Closed", "Total"],
                            datasets: [
                              {
                                data: [
                                  (data?.summary?.status_wise_count?.Open || data?.summary?.status_wise_count?.open || 0),
                                  (data?.summary?.status_wise_count?.Closed || data?.summary?.status_wise_count?.closed || 0),
                                  data?.summary?.total_remarks || 0,
                                ],
                                backgroundColor: [
                                  "#42a5f5",
                                  "#66bb6a",
                                  "#ef5350",
                                ],
                              },
                            ],
                          }}
                          options={{
                            plugins: {
                              legend: {
                                display: true,
                                position: "bottom",
                              },
                              datalabels: {
                                color: "#000", // Set data labels text color
                                font: {
                                  size: 12,
                                  weight: "bold",
                                },
                                formatter: (value) => value, // Display the value on the chart
                                anchor: "center", // Center the label inside the slice
                                align: "center", // Center the label inside the slice
                              },
                            },
                          }}
                        />
                      </Box>
                      {!data?.summary?.status_wise_count && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          align="center"
                          sx={{ marginTop: 2 }}
                        >
                          No data available for the status-wise summary.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </>
        ) : (
          <Typography>No data available</Typography>
        )}
      </Box>
    </>
  );
};

export default AgentOverallPerformanceComponent;
